import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { Line } from "react-chartjs-2";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import { get_config_udg } from "./config.js";
import { on_update_udg_callback } from "./updateCharLogic.js";

const CHART_MIN_HEIGHT = 360;
const CHART_MAX_HEIGHT = 550;

const buildDatasets = (udgk = [], udg = [], facts = [], pbrs = []) => [
	{
		label: "УДГК",
		data: udgk,
		fill: false,
		xAxisID: "x",
		tension: 0,
		borderColor: "#336699",
		borderWidth: 2,
	},
	{
		label: "УДГ",
		data: udg,
		fill: false,
		xAxisID: "x",
		tension: 0,
		borderColor: "#0099ff",
		borderWidth: 2,
	},
	{
		label: "Факт",
		data: facts,
		fill: false,
		xAxisID: "x",
		tension: 0,
		borderColor: "#ff3300",
		borderWidth: 2,
		spanGaps: true,
	},
	{
		label: "ПБР",
		data: pbrs,
		fill: false,
		xAxisID: "x",
		tension: 0,
		borderColor: "#ff9900",
		borderWidth: 2,
	},
];

const INITIAL_DATA = { labels: "", datasets: buildDatasets() };

// Стабильная ссылка обязательна — та же причина, что и у Rt3MinChart/GouChart:
const LINE_CHART_OPTIONS = { maintainAspectRatio: false };

/**
 * Главный Line-график. Принимает только данные и конфиг через
 * императивный ref (не через props на каждый рендер)
 */
const MainLineChart = forwardRef(function MainLineChart(_props, ref) {
	const chartRef = useRef(null);
	const containerRef = useRef(null);
	const [boxSize, setBoxSize] = useState({ width: 600, height: 360 });

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return undefined;

		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const width = entry.contentRect.width;
				setBoxSize({
					width: Math.max(360, width),
					height: Math.min(
						CHART_MAX_HEIGHT,
						Math.max(CHART_MIN_HEIGHT, width * 0.35),
					),
				});
				if (chartRef.current) {
					chartRef.current.resize();
				}
			}
		});

		resizeObserver.observe(container);
		return () => resizeObserver.disconnect();
	}, []);

	useImperativeHandle(ref, () => ({
		applyRealtimeUpdate(data, station) {
			const chart = chartRef.current;
			if (!chart) return;
			on_update_udg_callback(chart, data, station);
			chart.update();
		},
		setPlan(plan) {
			const chart = chartRef.current;
			if (!chart || !plan) return;
			chart.data.datasets[0].data = plan.pbr;
			chart.data.datasets[1].data = plan.udg;
			chart.data.datasets[3].data = plan.pbr;
			chart.update();
		},
		setPbrAnnotation(range) {
			const chart = chartRef.current;
			if (!chart) return;
			chart.options.plugins.annotation.annotations.pbr = range;
		},
		setStats({ udgk, udg, facts, pbrs, minv, maxv }) {
			const chart = chartRef.current;
			if (!chart) return;
			chart.data = { datasets: buildDatasets(udgk, udg, facts, pbrs) };
			chart.options = get_config_udg(minv, maxv);
			chart.update();
		},
		resetZoom() {
			const chart = chartRef.current;
			if (!chart) return;
			chart.options.scales.x.time.unit = "hour";
			chart.resetZoom();
		},
	}));

	const handleResetZoom = (event) => {
		if (chartRef.current && event.button === 0) {
			chartRef.current.options.scales.x.time.unit = "hour";
			chartRef.current.resetZoom();
		}
	};

	return (
		<div ref={containerRef}>
			<ResizableBox
				resizeHandles={["se"]}
				width={boxSize.width}
				height={boxSize.height}
				minConstraints={[360, 360]}
				style={{ paddingBottom: "30px" }}
			>
				<Line
					options={LINE_CHART_OPTIONS}
					onDoubleClick={handleResetZoom}
					style={{ height: "100%", width: "99%", margin: "auto" }}
					ref={chartRef}
					data={INITIAL_DATA}
				/>
			</ResizableBox>
		</div>
	);
});

// Без props, полностью управляется через ref — не должен перерендериваться
// из-за родителя.
export default React.memo(MainLineChart);
